import { WarningCircle } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import type { QueryWarning, ViewDto, ViewScope } from "@plannello/shared";
import { useNavigate } from "@tanstack/react-router";
import { type FormEvent, useEffect, useState } from "react";
import { Form } from "react-aria-components";
import { warningText } from "@/components/items/FilterBar";
import { groupLabel, parseGroupBy } from "@/components/items/grouping";
import type { ViewSearch } from "@/components/items/viewSearch";
import {
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  InlineError,
  Modal,
  Radio,
  RadioGroup,
  TextField,
} from "@/components/ui";
import { useCatalog } from "@/data/CatalogProvider";
import type { Product } from "@/data/types";
import { ApiError } from "@/lib/api";
import { toast } from "@/lib/toast";
import { createView, patchView, usePermissions, validateViewQuery } from "./api";
import { closeSaveView, type SaveRequest, useSaveViewState } from "./viewMeta";
import { type ItemLayout, layoutOf, searchToDisplay } from "./viewState";

const layoutName = (l: ItemLayout) =>
  ({ list: m.layout_list(), board: m.layout_board(), timeline: m.layout_timeline() })[l];

/**
 * Host for the "Save view" dialog of a product page (mounted once per page, opened through
 * `openSaveView`). Create captures the filter, layout and every display option; edit renames
 * and shares or unshares. Sharing needs `view.share` on the product; without it the option says
 * why it is off. Query warnings from the server's validator are shown before saving.
 */
export function SaveViewHost({
  product,
  layout,
  search,
}: {
  product: Product;
  layout: ItemLayout;
  search: ViewSearch;
}) {
  const { request, seq } = useSaveViewState();
  return (
    <Modal isOpen={!!request} onOpenChange={(o) => !o && closeSaveView()} size="md">
      {request ? (
        <SaveViewForm key={seq} request={request} product={product} layout={layout} search={search} />
      ) : (
        <span />
      )}
    </Modal>
  );
}

function SaveViewForm({
  request,
  product,
  layout,
  search,
}: {
  request: SaveRequest;
  product: Product;
  layout: ItemLayout;
  search: ViewSearch;
}) {
  const catalog = useCatalog();
  const navigate = useNavigate();
  const perms = usePermissions();
  const editing = request.mode === "edit" ? request.view : null;
  const canShare = perms.product("view.share", product.id);
  const [name, setName] = useState(editing?.name ?? "");
  const [scope, setScope] = useState<"personal" | "product">(
    editing && editing.scope !== "personal" ? "product" : "personal",
  );
  const [pin, setPin] = useState(editing?.pinned ?? false);
  const [warnings, setWarnings] = useState<QueryWarning[]>([]);
  const [error, setError] = useState<string>();
  const [nameError, setNameError] = useState<string>();
  const [pending, setPending] = useState(false);
  const query = editing ? editing.query : (search.q ?? "").trim();
  const viewLayout = editing ? layoutOf(editing.layout) : layout;

  useEffect(() => {
    if (!query) return;
    let live = true;
    validateViewQuery(query, scope === "personal" ? { scope: "personal" } : { scope: "product", scopeId: product.id })
      .then((w) => live && setWarnings(w))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [query, scope, product.id]);

  const group = parseGroupBy(search.group, catalog, "status");
  const summary = [
    layoutName(viewLayout),
    viewLayout !== "timeline" && group !== "none"
      ? m.views_summary_grouped({ group: groupLabel(group, catalog) })
      : null,
  ]
    .filter(Boolean)
    .join(", ");

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError(m.validation_required());
      return;
    }
    setPending(true);
    setError(undefined);
    const target: { scope: ViewScope; scopeId: string | null } =
      scope === "personal" ? { scope: "personal", scopeId: null } : { scope: "product", scopeId: product.id };
    try {
      let saved: ViewDto;
      if (editing) {
        const moving = (editing.scope === "personal") !== (scope === "personal");
        saved = await patchView(editing.id, { name: trimmed, ...(moving ? target : {}) });
        toast.show({ title: m.views_updated({ name: trimmed }) });
      } else {
        saved = await createView({
          name: trimmed,
          ...target,
          query,
          layout,
          display: searchToDisplay(search, product.key),
          pinned: pin,
        });
        toast.show({ title: m.views_saved({ name: trimmed }), tone: "success" });
        void navigate({ to: ".", search: (prev: ViewSearch) => ({ ...prev, view: saved.id }), replace: true } as never);
      }
      closeSaveView();
    } catch (err) {
      setError(err instanceof ApiError && err.status === 403 ? m.views_share_denied() : m.toast_update_failed());
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog aria-labelledby="save-view-title">
      <Form onSubmit={submit} className="flex min-h-0 flex-col">
        <DialogHeader
          title={<span id="save-view-title">{editing ? m.views_edit_title() : m.views_save_title()}</span>}
          onClose={closeSaveView}
        />
        <DialogBody className="flex flex-col gap-4">
          <TextField
            label={m.views_name()}
            value={name}
            onChange={(v) => {
              setName(v);
              setNameError(undefined);
            }}
            error={nameError}
            autoFocus
            maxLength={120}
          />
          <RadioGroup
            label={m.views_visibility()}
            value={scope}
            onChange={(v) => setScope(v as "personal" | "product")}
          >
            <Radio value="personal" label={m.views_scope_personal()} description={m.views_scope_personal_desc()} />
            <Radio
              value="product"
              label={m.views_scope_product({ product: product.isSpaceProduct ? m.space_wide() : product.name })}
              description={
                canShare || scope === "product" ? m.views_scope_product_desc() : m.views_scope_needs_permission()
              }
              isDisabled={!canShare && scope !== "product"}
            />
          </RadioGroup>
          {editing ? null : (
            <Checkbox isSelected={pin} onChange={setPin}>
              {m.views_pin_on_save()}
            </Checkbox>
          )}
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-control border border-line bg-subtle px-3 py-2 text-xs">
            <dt className="text-fg-subtle">{m.views_summary_filter()}</dt>
            <dd className="min-w-0 truncate-title font-mono text-fg" title={query}>
              {query || m.views_summary_no_filter()}
            </dd>
            <dt className="text-fg-subtle">{m.views_summary_layout()}</dt>
            <dd className="min-w-0 text-fg">{summary}</dd>
          </dl>
          {warnings.length > 0 ? (
            <div role="status" className="flex flex-col gap-1 text-xs text-warning-text">
              {warnings.map((w) => (
                <p key={`${w.code}:${w.raw}`} className="flex items-start gap-1.5">
                  <WarningCircle size={14} weight="fill" aria-hidden="true" className="mt-px shrink-0" />
                  <span className="min-w-0">{warningText(w)}</span>
                </p>
              ))}
              <p className="text-fg-muted">{m.views_warnings_hint()}</p>
            </div>
          ) : null}
          {error ? <InlineError message={error} /> : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onPress={closeSaveView}>
            {m.common_cancel()}
          </Button>
          <Button type="submit" variant="primary" isPending={pending}>
            {editing ? m.common_save() : m.views_save()}
          </Button>
        </DialogFooter>
      </Form>
    </Dialog>
  );
}
